import * as React from "react";
import { twMerge } from "tailwind-merge";
import Text from "./Text";

export type ChipType =
  | "success"
  | "warning"
  | "critical"
  | "neutral"
  | "info"
  | "caution"
  | "verified"
  | "primary"
  | "secondary";

export type ChipSize = "sm" | "md" | "lg";

export type ChipProps = {
  label: string;
  type?: ChipType;
  size?: ChipSize;
  solid?: boolean;
  icon?: React.ReactNode;
  className?: string;
};

const typeClass: Record<ChipType, { outline: string; solid: string }> = {
  success: {
    outline: "border-[#B7EB8F] bg-[#F6FFED] text-[#52C41A]",
    solid: "border-[#008937] bg-[#008937] text-white",
  },
  warning: {
    outline: "border-[#FFD591] bg-[#FFF7E6] text-[#FA8C16]",
    solid: "border-[#FA8C16] bg-[#FA8C16] text-white",
  },
  critical: {
    outline: "border-[#FFA39E] bg-[#FFF1F0] text-[#F5222D]",
    solid: "border-[#F5222D] bg-[#F5222D] text-white",
  },
  neutral: {
    outline: "border-[#D9D9D9] bg-[#FAFAFA] text-[#252525]",
    solid: "border-[#252525] bg-[#252525] text-white",
  },
  info: {
    outline: "border-[#91D5FF] bg-[#E6F7FF] text-[#1890FF]",
    solid: "border-[#1890FF] bg-[#1890FF] text-white",
  },
  caution: {
    outline: "border-[#FFE58F] bg-[#FFFBE6] text-[#FAAD14]",
    solid: "border-[#FAAD14] bg-[#FAAD14] text-white",
  },
  verified: {
    outline: "border-cyan-600 bg-cyan-50 text-cyan-800",
    solid: "border-cyan-600 bg-cyan-600 text-white",
  },
  primary: {
    outline: "border-primary bg-primary/10 text-text-primary",
    solid: "border-primary bg-primary text-text-primary",
  },
  secondary: {
    outline: "border-secondary bg-secondary/10 text-secondary",
    solid: "border-secondary bg-secondary text-text-surface",
  },
};

const sizeClass: Record<ChipSize, string> = {
  sm: "h-[22px] gap-[4px] px-[8px] text-[11px]",
  md: "h-[26px] gap-[6px] px-[10px] text-[12px]",
  lg: "h-[32px] gap-[8px] px-[12px] text-[13px]",
};

export default function Chip({
  label,
  type = "neutral",
  size = "sm",
  solid = false,
  icon,
  className,
}: ChipProps) {
  return (
    <span
      className={twMerge(
        "inline-flex max-w-full items-center rounded-[4px] border font-[600] tracking-[0.02em]",
        typeClass[type][solid ? "solid" : "outline"],
        sizeClass[size],
        className,
      )}
    >
      {icon ? (
        <span className="inline-flex shrink-0 items-center">{icon}</span>
      ) : null}
      <Text as="span" level={5} className="truncate font-[600]">
        {label}
      </Text>
    </span>
  );
}
